import { Checkbox, DatePicker, Grid, GridColumn, GridRow, Input, Select, SelectOptionType } from '@bp/ui-components';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { SingleValue } from 'react-select';
import { Frequency, RRule, rrulestr } from 'rrule';
import { useGetHolidays } from '../../../hooks/useGetHolidays';
import { buildRRule } from '../../../utils/buildRRule';
import { isFirstBeforeSecond } from '../../../utils/dateCalculations';

const recurrenceOptions: SelectOptionType[] = [
  {
    label: t('recurrence.daily'),
    value: RRule.DAILY,
  },
  {
    label: t('recurrence.weekly'),
    value: RRule.WEEKLY,
  },
  {
    label: t('recurrence.monthly'),
    value: RRule.MONTHLY,
  },
];

const weekdayOptions: SelectOptionType[] = [
  { label: t('recurrence.monday', { context: 'short' }), value: 0 },
  { label: t('recurrence.tuesday', { context: 'short' }), value: 1 },
  { label: t('recurrence.wednesday', { context: 'short' }), value: 2 },
  { label: t('recurrence.thursday', { context: 'short' }), value: 3 },
  { label: t('recurrence.friday', { context: 'short' }), value: 4 },
  { label: t('recurrence.saturday', { context: 'short' }), value: 5 },
  { label: t('recurrence.sunday', { context: 'short' }), value: 6 },
];

type RecurringEventFormProps = {
  rrule?: string | null;
  excludeHolidays: boolean;
  start: Date;
  allowRecurring: boolean;
  onChange: (rrule: string | null) => void;
  isRecurrenceAllowed?: (frequency: Frequency | null) => boolean;
  onExcludeHolidaysChange: (value: boolean) => void;
};

export const RecurringEventForm = observer(
  ({
    rrule,
    onChange,
    start,
    excludeHolidays,
    onExcludeHolidaysChange,
    isRecurrenceAllowed,
    allowRecurring,
  }: RecurringEventFormProps) => {
    const { holidays, schoolYear } = useGetHolidays();
    const [recurrence, setRecurrence] = useState<SelectOptionType | null>(null);
    const [interval, setInterval] = useState<number>(1);
    const [weekdays, setWeekdays] = useState<SelectOptionType[]>([]);
    const lastSentRRule = useRef<string | null>(null);
    const [repeatUntil, setLastRepetition] = useState<Date | null>(schoolYear?.end ? new Date(schoolYear.end) : null);

    useEffect(() => {
      if (rrule && rrule !== lastSentRRule.current) {
        const data = rrulestr(rrule);
        if (!data.options.dtstart) {
          return;
        }

        const freq = recurrenceOptions.find((ro) => ro.value === data.options.freq);
        if (freq) {
          setRecurrence(freq);
        }
        if (data.options.byweekday) {
          const wds = Array.isArray(data.options.byweekday) ? data.options.byweekday : [data.options.byweekday];
          const selectedWeekdays = weekdayOptions.filter((wd) => wds.some((d) => d === wd.value));
          setWeekdays(selectedWeekdays);
        }
        // beim Bearbeiten einer Serie: Intervall und letzte Wiederholung übernehmen
        if (data.options.interval) {
          setInterval(data.options.interval);
        }
        if (data.options.until) {
          setLastRepetition(dayjs(data.options.until).startOf('day').toDate());
        }
      }
    }, [rrule]);

    useEffect(() => {
      const rruleObj = buildRRule({
        start: start,
        recurrence,
        weekdays,
        repeatUntil,
        interval,
      });
      const newRRuleString = rruleObj ? rruleObj.toString() : null;

      if (newRRuleString !== lastSentRRule.current) {
        lastSentRRule.current = newRRuleString;
        onChange(newRRuleString);
      }
    }, [start, recurrence, weekdays, repeatUntil, interval, onChange]);

    const rruleObj = rrule ? rrulestr(rrule) : null;

    const _holidays = holidays?.sort((a, b) => (isFirstBeforeSecond(a.start, b.start) ? -1 : 1));
    const exclude: Date[] = [];

    _holidays?.forEach((holiday) => {
      // set HH:MM from start to exclude holidays
      const hstart = new Date(holiday.start);
      const end = new Date(holiday.end);
      if (excludeHolidays) {
        for (let d = new Date(hstart); d <= end; d.setDate(d.getDate() + 1)) {
          // Uhrzeit von 'start' übernehmen
          d.setHours(start.getHours(), start.getMinutes(), start.getSeconds(), start.getMilliseconds());
          exclude.push(new Date(d));
        }
      }
    });

    return (
      <Grid useFormGap>
        <GridRow spacingBottom={'s'}>
          <GridColumn width={3}>
            <Select
              isClearable={true}
              label={t('recurrence.title')}
              options={recurrenceOptions.filter((r) => {
                return isRecurrenceAllowed ? isRecurrenceAllowed(r.value as Frequency) : false;
              })}
              disabled={!allowRecurring}
              name={'recurrence'}
              value={recurrence}
              onChange={(event) => {
                if (event === null) {
                  setRecurrence(null);
                } else {
                  const e = event as SingleValue<SelectOptionType>;
                  const val = recurrenceOptions.find((w) => w.value === e?.value);
                  if (val) {
                    setRecurrence(val);
                  }
                }
              }}
            />
          </GridColumn>
          <GridColumn width={3}>
            <DatePicker
              disabled={!recurrence}
              minDate={schoolYear?.start ? new Date(schoolYear.start) : undefined}
              maxDate={schoolYear?.end ? new Date(schoolYear.end) : undefined}
              name='lastRepetition'
              label={t('recurrence.lastRepetition')}
              onChange={(e) => {
                setLastRepetition(e);
              }}
              value={repeatUntil ?? undefined}
            />
          </GridColumn>
          <GridColumn width={3}>
            <Input
              disabled={!recurrence}
              tooltipText={t('recurrence.intervalTooltip', { max: 1000 })}
              name={'interval'}
              type='number'
              min={0}
              label={t('recurrence.interval')}
              value={interval}
              onChange={(event) => {
                const val = Number(event.target.value);
                setInterval(val);
              }}
            />
          </GridColumn>
          <GridColumn width={3}>
            <Checkbox
              disabled={!recurrence}
              className={'mt-4'}
              name='excludeHolidays'
              label={t('schoolEvents.excludeHolidays')}
              checked={excludeHolidays}
              onChange={(e) => {
                onExcludeHolidaysChange(e.target.checked);
              }}
            />
          </GridColumn>
        </GridRow>
        <GridRow spacingTop={'s'}>
          <GridColumn width={12}>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              {weekdayOptions.map((weekday) => {
                const label = weekday.label ?? '';
                return (
                  <Checkbox
                    disabled={recurrence?.value !== RRule.WEEKLY}
                    className={'mt-4'}
                    name={label.toLocaleLowerCase()}
                    label={label}
                    key={weekday.value as string}
                    checked={weekdays.some((w) => w.value === weekday.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setWeekdays((prev) => [...prev, weekday]);
                      } else {
                        setWeekdays((prev) => prev.filter((w) => w.value !== weekday.value));
                      }
                    }}
                  />
                );
              })}
            </div>
          </GridColumn>
        </GridRow>
        {rruleObj && (
          <GridRow>
            <GridColumn width={12} className={'readonly-blend'}>
              <>
                <div>{t('recurrence.previewText')}</div>
                <DatePicker
                  holidayStyling={'outOfMonth'}
                  monthsShown={4}
                  showMonthYearDropdown={false}
                  inline={true}
                  readonly={true}
                  embedded={true}
                  dense={true}
                  name='showRecurrenceEvents'
                  label={t('recurrence.previewLabel')}
                  holidays={exclude.map((e) => {
                    return { date: e, holidayName: t('holidays.title', { count: 1 }) };
                  })}
                  highlightDates={rruleObj?.all().filter((d) => {
                    const djs = dayjs.utc(d).format('YYYY-MM-DD');
                    return !exclude.find((ex) => dayjs(ex).format('YYYY-MM-DD') === djs);
                  })}
                  minDate={start}
                  maxDate={repeatUntil ?? undefined}
                />
              </>
            </GridColumn>
          </GridRow>
        )}
      </Grid>
    );
  },
);
